import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { platformAPI } from '../../services/api';
import { Modal, LoadingSpinner } from '../../components/ui';
import { EmptyState } from '../../components/ui/AdminPrimitives';
import { useAuth } from '../../hooks/useAuth';
import {
  PlatformKpiCards,
  PlatformTenantToolbar,
  TenantAccordionItem,
  SuspendTenantDialog,
  CreateTenantAdminModal,
  ResetAdminPasswordDialog,
  type PlatformTenant,
  type TenantUserRow,
  type TenantSortOption,
  type TenantStatusFilter,
} from '../../components/platform';

const PlatformFunerarias: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth() as any;
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<TenantStatusFilter>('all');
  const [sortBy, setSortBy] = useState<TenantSortOption>('name');

  const [tenantModal, setTenantModal] = useState(false);
  const [editTenant, setEditTenant] = useState<PlatformTenant | null>(null);
  const [tenantForm, setTenantForm] = useState({
    name: '',
    slug: '',
    contactName: '',
    contactEmail: '',
    phone: '',
    notes: '',
    isActive: true,
    billingMode: 'new' as 'new' | 'existing',
    organizationId: '',
    organizationName: '',
    planTier: 'BASIC' as 'BASIC' | 'PROFESSIONAL' | 'PREMIUM',
  });

  const [userModal, setUserModal] = useState<{ tenantId: string; name: string } | null>(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'ADMIN' as 'ADMIN' | 'MANAGER' | 'EMPLOYEE',
  });

  const [pwdModal, setPwdModal] = useState<{ tenantId: string; userId: string } | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [suspendTenant, setSuspendTenant] = useState<PlatformTenant | null>(null);
  const [createAdminTenant, setCreateAdminTenant] = useState<PlatformTenant | null>(null);
  const [resetAdminTenant, setResetAdminTenant] = useState<PlatformTenant | null>(null);
  const [resetAdminTargetUserId, setResetAdminTargetUserId] = useState<string | null>(null);
  const [resetAdminOptions, setResetAdminOptions] = useState<TenantUserRow[]>([]);
  const [lastGeneratedPassword, setLastGeneratedPassword] = useState<string | null>(null);

  const { data: statsRes, isLoading: statsLoading } = useQuery(['platform-stats'], () =>
    platformAPI.getStats()
  );
  const stats = statsRes?.data?.data as
    | { funerariasTotal: number; funerariasActivas: number; usuariosEnFunerarias: number }
    | undefined;

  const { data: tenantsRes, isLoading } = useQuery(
    ['platform-tenants', search],
    () => platformAPI.listTenants({ search: search || undefined }),
    { keepPreviousData: true }
  );

  const { data: orgsRes } = useQuery(['platform-organizations'], () =>
    platformAPI.listOrganizations()
  );
  const organizations = (orgsRes?.data?.data || []) as Array<{
    id: string;
    name: string;
    tier: string;
    planLabel: string;
    tenantCount: number;
    limits: { maxTenants: number | null };
  }>;

  const tenants = (tenantsRes?.data?.data || []) as PlatformTenant[];

  const filteredTenants = useMemo(() => {
    let list = [...tenants];
    if (statusFilter === 'active') list = list.filter((t) => t.isActive);
    if (statusFilter === 'inactive') list = list.filter((t) => !t.isActive);

    if (sortBy === 'name') {
      list.sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
    } else if (sortBy === 'users_desc') {
      list.sort((a, b) => b.userCount - a.userCount);
    } else if (sortBy === 'created_desc') {
      list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
    return list;
  }, [tenants, statusFilter, sortBy]);

  const { data: usersRes, isLoading: usersLoading } = useQuery(
    ['platform-tenant-users', expandedId],
    () => platformAPI.listTenantUsers(expandedId!),
    { enabled: !!expandedId }
  );
  const tenantUsers = (usersRes?.data?.data || []) as TenantUserRow[];
  const resolveTenantAdmins = async (tenantId: string) => {
    if (expandedId === tenantId && tenantUsers.length > 0) {
      return tenantUsers.filter((u) => u.role === 'ADMIN');
    }
    const res = await platformAPI.listTenantUsers(tenantId);
    const users = (res?.data?.data || []) as TenantUserRow[];
    return users.filter((u) => u.role === 'ADMIN');
  };

  const createTenant = useMutation(
    (body: Record<string, unknown>) => platformAPI.createTenant(body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['platform-tenants']);
        queryClient.invalidateQueries(['platform-organizations']);
        queryClient.invalidateQueries(['platform-stats']);
        toast.success('Funeraria creada');
        setTenantModal(false);
        resetTenantForm();
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Error al crear');
      },
    }
  );

  const updateTenant = useMutation(
    ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      platformAPI.updateTenant(id, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['platform-tenants']);
        toast.success('Funeraria actualizada');
        setEditTenant(null);
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Error al guardar');
      },
    }
  );

  const createUser = useMutation(
    ({
      tenantId,
      body,
    }: {
      tenantId: string;
      body: { name: string; email: string; password: string; role: string };
    }) => platformAPI.createTenantUser(tenantId, body),
    {
      onSuccess: (_, v) => {
        queryClient.invalidateQueries(['platform-tenant-users']);
        queryClient.invalidateQueries(['platform-tenants']);
        queryClient.invalidateQueries(['platform-organizations']);
        queryClient.invalidateQueries(['platform-stats']);
        toast.success('Usuario creado');
        setUserModal(null);
        setExpandedId(v.tenantId);
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Error');
      },
    }
  );

  const toggleUser = useMutation(
    ({ tenantId, userId }: { tenantId: string; userId: string }) =>
      platformAPI.toggleTenantUserStatus(tenantId, userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['platform-tenant-users']);
        toast.success('Estado actualizado');
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Error');
      },
    }
  );

  const resetPwd = useMutation(
    ({
      tenantId,
      userId,
      newPassword: pwd,
    }: {
      tenantId: string;
      userId: string;
      newPassword: string;
    }) => platformAPI.resetTenantUserPassword(tenantId, userId, { mode: 'TEMP_PASSWORD', newPassword: pwd }),
    {
      onSuccess: () => {
        toast.success('Contraseña actualizada');
        setPwdModal(null);
        setNewPassword('');
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Error');
      },
    }
  );

  const deleteUser = useMutation(
    ({ tenantId, userId }: { tenantId: string; userId: string }) =>
      platformAPI.deleteTenantUser(tenantId, userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['platform-tenant-users']);
        queryClient.invalidateQueries(['platform-tenants']);
        queryClient.invalidateQueries(['platform-organizations']);
        queryClient.invalidateQueries(['platform-stats']);
        toast.success('Usuario eliminado');
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Error');
      },
    }
  );

  const updateTenantStatus = useMutation(
    ({ tenantId, isActive, reason }: { tenantId: string; isActive: boolean; reason?: string }) =>
      platformAPI.updateTenantStatus(tenantId, { isActive, reason }),
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries(['platform-tenants']);
        queryClient.invalidateQueries(['platform-organizations']);
        queryClient.invalidateQueries(['platform-stats']);
        toast.success(vars.isActive ? 'Funeraria reactivada' : 'Funeraria suspendida');
        setSuspendTenant(null);
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Error al cambiar estado');
      }
    }
  );

  const resendCredentials = useMutation(
    ({ tenantId, userId }: { tenantId: string; userId: string }) =>
      platformAPI.resendTenantCredentials(tenantId, userId),
    {
      onSuccess: () => toast.success('Credenciales reenviadas'),
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || err?.response?.data?.message || 'No se pudo reenviar');
      }
    }
  );

  const resetAdminPassword = useMutation(
    ({
      tenantId,
      userId,
      mode,
      newPassword: nextPassword
    }: {
      tenantId: string;
      userId: string;
      mode: 'TEMP_PASSWORD' | 'RESET_EMAIL';
      newPassword?: string;
    }) =>
      platformAPI.resetTenantUserPassword(tenantId, userId, {
        mode,
        newPassword: nextPassword
      }),
    {
      onSuccess: (res) => {
        const generated = res?.data?.data?.temporaryPassword || null;
        setLastGeneratedPassword(generated);
        toast.success(res?.data?.message || 'Reset aplicado');
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || err?.response?.data?.message || 'No se pudo resetear');
      }
    }
  );

  const startSupportSession = useMutation(
    ({ tenantId, targetUserId }: { tenantId: string; targetUserId?: string }) =>
      platformAPI.startTenantImpersonation(tenantId, { targetUserId }),
    {
      onSuccess: async (res) => {
        const payload = res?.data?.data;
        if (!payload?.token) {
          toast.error('No se pudo iniciar sesión de soporte');
          return;
        }
        await startImpersonation(payload);
        toast.success('Modo soporte iniciado');
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || err?.response?.data?.message || 'No se pudo impersonar');
      }
    }
  );

  const resetTenantForm = () => {
    setTenantForm({
      name: '',
      slug: '',
      contactName: '',
      contactEmail: '',
      phone: '',
      notes: '',
      isActive: true,
      billingMode: 'new',
      organizationId: '',
      organizationName: '',
      planTier: 'BASIC',
    });
  };

  const openCreateTenant = () => {
    resetTenantForm();
    setTenantModal(true);
  };

  const openEditTenant = (t: PlatformTenant) => {
    setTenantForm({
      name: t.name,
      slug: t.slug,
      contactName: t.contactName || '',
      contactEmail: t.contactEmail || '',
      phone: t.phone || '',
      notes: t.notes || '',
      isActive: t.isActive
    });
    setEditTenant(t);
  };

  const openCreateAdmin = (tenant: PlatformTenant) => {
    setCreateAdminTenant(tenant);
    setExpandedId(tenant.id);
  };

  const openResetAdminDialog = async (tenant: PlatformTenant) => {
    const admins = await resolveTenantAdmins(tenant.id);
    if (admins.length === 0) {
      toast.error('Esta funeraria no tiene usuarios admin');
      return;
    }
    setResetAdminOptions(admins);
    setResetAdminTargetUserId(admins[0].id);
    setResetAdminTenant(tenant);
  };

  const runImpersonation = async (tenant: PlatformTenant) => {
    const admins = await resolveTenantAdmins(tenant.id);
    const target = admins[0];
    if (!target) {
      toast.error('No hay administradores para impersonar');
      return;
    }
    startSupportSession.mutate({ tenantId: tenant.id, targetUserId: target.id });
  };

  const runResendCredentials = async (tenant: PlatformTenant) => {
    const admins = await resolveTenantAdmins(tenant.id);
    const target = admins[0];
    if (!target) {
      toast.error('No hay administradores para reenviar credenciales');
      return;
    }
    resendCredentials.mutate({ tenantId: tenant.id, userId: target.id });
  };

  const listEmpty = !isLoading && tenants.length === 0;
  const filteredEmpty = !isLoading && tenants.length > 0 && filteredTenants.length === 0;

  return (
    <div className="space-y-8">
      <section className="rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm ring-1 ring-slate-900/5 sm:p-8">
        <div className="max-w-3xl">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
            Operaciones
          </p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Funerarias clientes
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Gestiona espacios independientes (tenants), usuarios por funeraria y datos de contacto
            comercial. Esta consola es para administración de plataforma, no para operar una funeraria
            concreta.
          </p>
          <Link
            to="/platform/plans"
            className="mt-4 inline-flex text-sm font-semibold text-slate-800 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-600"
          >
            Ir a planes y cuentas de facturación →
          </Link>
        </div>
      </section>

      <PlatformKpiCards
        stats={
          stats ?? {
            funerariasTotal: 0,
            funerariasActivas: 0,
            usuariosEnFunerarias: 0,
          }
        }
        isLoading={statsLoading}
      />

      <PlatformTenantToolbar
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        sortBy={sortBy}
        onSortChange={setSortBy}
        onNewTenant={openCreateTenant}
      />

      <section className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/5">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <LoadingSpinner />
            <p className="text-sm font-medium text-slate-500">Cargando funerarias…</p>
          </div>
        ) : listEmpty ? (
          <EmptyState
            title="No hay funerarias registradas"
            description="Crea la primera funeraria con el botón «Nueva funeraria» para comenzar a incorporar clientes."
          />
        ) : filteredEmpty ? (
          <div className="py-14 text-center">
            <p className="text-sm font-semibold text-slate-800">Ningún resultado con estos filtros</p>
            <p className="mt-1 text-sm text-slate-500">
              Cambia el filtro de estado o la búsqueda para ver funerarias.
            </p>
          </div>
        ) : (
          <ul className="space-y-4 p-4 sm:p-5">
            {filteredTenants.map((t) => {
              const open = expandedId === t.id;
              return (
                <TenantAccordionItem
                  key={t.id}
                  tenant={t}
                  open={open}
                  onToggle={() => setExpandedId(open ? null : t.id)}
                  users={open ? tenantUsers : []}
                  usersLoading={open && usersLoading}
                  onEdit={() => openEditTenant(t)}
                  onViewDetail={() => navigate(`/platform/tenants/${t.id}`)}
                  onCreateAdmin={() => openCreateAdmin(t)}
                  onToggleTenantStatus={() => setSuspendTenant(t)}
                  onImpersonateTenant={() => runImpersonation(t)}
                  onResetAdminPassword={() => openResetAdminDialog(t)}
                  onResendCredentials={() => runResendCredentials(t)}
                  onViewIncidents={() => navigate(`/platform/incidents?tenantId=${t.id}`)}
                  onViewAudit={() => navigate(`/platform/tenants/${t.id}?tab=audit`)}
                  onToggleUserStatus={(userId) =>
                    toggleUser.mutate({ tenantId: t.id, userId })
                  }
                  onResetPassword={(userId) =>
                    setPwdModal({ tenantId: t.id, userId })
                  }
                  onDeleteUser={(u) => {
                    if (
                      window.confirm(
                        `¿Eliminar usuario ${u.email}? Esta acción no se puede deshacer.`
                      )
                    ) {
                      deleteUser.mutate({ tenantId: t.id, userId: u.id });
                    }
                  }}
                  toggleUserPending={toggleUser.isLoading}
                />
              );
            })}
          </ul>
        )}
      </section>

      <SuspendTenantDialog
        tenant={suspendTenant}
        isSubmitting={updateTenantStatus.isLoading}
        onClose={() => setSuspendTenant(null)}
        onConfirm={({ isActive, reason }) =>
          updateTenantStatus.mutate({
            tenantId: suspendTenant!.id,
            isActive,
            reason
          })
        }
      />

      <CreateTenantAdminModal
        tenant={createAdminTenant}
        isSubmitting={createUser.isLoading}
        onClose={() => setCreateAdminTenant(null)}
        onSubmit={(payload) => {
          if (!createAdminTenant) return;
          createUser.mutate(
            { tenantId: createAdminTenant.id, body: payload },
            {
              onSuccess: () => {
                setCreateAdminTenant(null);
                setExpandedId(createAdminTenant.id);
              }
            }
          );
        }}
      />

      <ResetAdminPasswordDialog
        isOpen={Boolean(resetAdminTenant)}
        admins={resetAdminOptions}
        selectedUserId={resetAdminTargetUserId}
        generatedPassword={lastGeneratedPassword}
        isSubmitting={resetAdminPassword.isLoading}
        onClose={() => {
          setResetAdminTenant(null);
          setLastGeneratedPassword(null);
          setResetAdminTargetUserId(null);
          setResetAdminOptions([]);
        }}
        onSubmit={({ userId, mode, newPassword: generated }) => {
          if (!resetAdminTenant) return;
          resetAdminPassword.mutate({
            tenantId: resetAdminTenant.id,
            userId,
            mode,
            newPassword: generated
          });
        }}
      />

      <Modal
        isOpen={tenantModal}
        onClose={() => setTenantModal(false)}
        title="Nueva funeraria"
        size="lg"
      >
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            createTenant.mutate({
              name: tenantForm.name,
              slug: tenantForm.slug,
              isActive: tenantForm.isActive,
              contactName: tenantForm.contactName || undefined,
              contactEmail: tenantForm.contactEmail || undefined,
              phone: tenantForm.phone || undefined,
              notes: tenantForm.notes || undefined,
              ...(tenantForm.billingMode === 'existing' && tenantForm.organizationId
                ? { organizationId: tenantForm.organizationId }
                : {
                    organizationName: tenantForm.organizationName || tenantForm.name,
                    planTier: tenantForm.planTier,
                  }),
            });
          }}
        >
          <fieldset className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-3">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Cuenta de facturación
            </legend>
            <div className="flex flex-wrap gap-4 text-sm text-slate-700">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="billingMode"
                  checked={tenantForm.billingMode === 'new'}
                  onChange={() => setTenantForm((f) => ({ ...f, billingMode: 'new' }))}
                />
                Nueva cuenta
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="billingMode"
                  checked={tenantForm.billingMode === 'existing'}
                  onChange={() => setTenantForm((f) => ({ ...f, billingMode: 'existing' }))}
                />
                Agregar a cuenta existente
              </label>
            </div>
            {tenantForm.billingMode === 'existing' ? (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Organización</label>
                <select
                  required
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
                  value={tenantForm.organizationId}
                  onChange={(e) => setTenantForm((f) => ({ ...f, organizationId: e.target.value }))}
                >
                  <option value="">Seleccione…</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name} — {org.planLabel} ({org.tenantCount}/
                      {org.limits.maxTenants ?? '∞'} funerarias)
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Nombre de la cuenta
                  </label>
                  <input
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Ej. Grupo Funerario Norte"
                    value={tenantForm.organizationName}
                    onChange={(e) =>
                      setTenantForm((f) => ({ ...f, organizationName: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Plan</label>
                  <select
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
                    value={tenantForm.planTier}
                    onChange={(e) =>
                      setTenantForm((f) => ({
                        ...f,
                        planTier: e.target.value as 'BASIC' | 'PROFESSIONAL' | 'PREMIUM',
                      }))
                    }
                  >
                    <option value="BASIC">Básico — $20/mes (2 funerarias)</option>
                    <option value="PROFESSIONAL">Profesional — $50/mes (5 funerarias)</option>
                    <option value="PREMIUM">Premium — $100/mes (ilimitado)</option>
                  </select>
                </div>
              </div>
            )}
          </fieldset>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Nombre comercial</label>
              <input
                required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={tenantForm.name}
                onChange={(e) => setTenantForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Identificador (slug)
              </label>
              <input
                required
                placeholder="ej. funeraria-norte"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                value={tenantForm.slug}
                onChange={(e) =>
                  setTenantForm((f) => ({
                    ...f,
                    slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                  }))
                }
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Contacto (nombre)
              </label>
              <input
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={tenantForm.contactName}
                onChange={(e) => setTenantForm((f) => ({ ...f, contactName: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Contacto (email)
              </label>
              <input
                type="email"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={tenantForm.contactEmail}
                onChange={(e) => setTenantForm((f) => ({ ...f, contactEmail: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Teléfono</label>
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={tenantForm.phone}
              onChange={(e) => setTenantForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Notas (ventas)</label>
            <textarea
              rows={3}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={tenantForm.notes}
              onChange={(e) => setTenantForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={tenantForm.isActive}
              onChange={(e) => setTenantForm((f) => ({ ...f, isActive: e.target.checked }))}
            />
            Funeraria activa
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
              onClick={() => setTenantModal(false)}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createTenant.isLoading}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
            >
              Crear
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!editTenant}
        onClose={() => setEditTenant(null)}
        title="Editar funeraria"
        size="lg"
      >
        {editTenant && (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              updateTenant.mutate({
                id: editTenant.id,
                body: {
                  name: tenantForm.name,
                  slug: tenantForm.slug,
                  isActive: tenantForm.isActive,
                  contactName: tenantForm.contactName || null,
                  contactEmail: tenantForm.contactEmail || null,
                  phone: tenantForm.phone || null,
                  notes: tenantForm.notes || null,
                },
              });
            }}
          >
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Nombre</label>
                <input
                  required
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={tenantForm.name}
                  onChange={(e) => setTenantForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Slug</label>
                <input
                  required
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                  value={tenantForm.slug}
                  onChange={(e) =>
                    setTenantForm((f) => ({
                      ...f,
                      slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
                    }))
                  }
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Contacto</label>
                <input
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={tenantForm.contactName}
                  onChange={(e) => setTenantForm((f) => ({ ...f, contactName: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Email contacto</label>
                <input
                  type="email"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={tenantForm.contactEmail}
                  onChange={(e) => setTenantForm((f) => ({ ...f, contactEmail: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Teléfono</label>
              <input
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={tenantForm.phone}
                onChange={(e) => setTenantForm((f) => ({ ...f, phone: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Notas</label>
              <textarea
                rows={3}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={tenantForm.notes}
                onChange={(e) => setTenantForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={tenantForm.isActive}
                onChange={(e) => setTenantForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
              Activa
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm text-slate-600"
                onClick={() => setEditTenant(null)}
              >
                Cerrar
              </button>
              <button
                type="submit"
                disabled={updateTenant.isLoading}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white"
              >
                Guardar
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={!!userModal}
        onClose={() => setUserModal(null)}
        title={userModal ? `Nuevo usuario — ${userModal.name}` : ''}
      >
        {userModal && (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              createUser.mutate({
                tenantId: userModal.tenantId,
                body: {
                  name: userForm.name,
                  email: userForm.email,
                  password: userForm.password,
                  role: userForm.role,
                },
              });
            }}
          >
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Nombre</label>
              <input
                required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={userForm.name}
                onChange={(e) => setUserForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Email</label>
              <input
                required
                type="email"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={userForm.email}
                onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Contraseña inicial</label>
              <input
                required
                type="password"
                minLength={6}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={userForm.password}
                onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Rol</label>
              <select
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={userForm.role}
                onChange={(e) =>
                  setUserForm((f) => ({
                    ...f,
                    role: e.target.value as 'ADMIN' | 'MANAGER' | 'EMPLOYEE',
                  }))
                }
              >
                <option value="ADMIN">Administrador (funeraria)</option>
                <option value="MANAGER">Gerente</option>
                <option value="EMPLOYEE">Empleado</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm text-slate-600"
                onClick={() => setUserModal(null)}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createUser.isLoading}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white"
              >
                Crear usuario
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={!!pwdModal}
        onClose={() => {
          setPwdModal(null);
          setNewPassword('');
        }}
        title="Nueva contraseña"
      >
        {pwdModal && (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              resetPwd.mutate({
                tenantId: pwdModal.tenantId,
                userId: pwdModal.userId,
                newPassword,
              });
            }}
          >
            <input
              required
              type="password"
              minLength={6}
              placeholder="Mínimo 6 caracteres"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button type="submit" className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white">
                Guardar
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default PlatformFunerarias;
